import React, { useState, useEffect, useRef } from 'react';
import {
    PackagePlus, Search, Warehouse, Save, RefreshCw,
    Plus, X, Loader2, CheckCircle,
    ChevronDown, Box, List, ClipboardList
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const today = () => new Date().toISOString().split('T')[0];

const emptyItem = () => ({
    itemCode: '',
    itemDescription: '',
    batchNo: '',
    warehouseCode: '',
    warehouseId: null,
    // qty — single source of truth, syncs to availableQty + itemDirect_qty
    quantity: '',
    availableQty: '',
    reservedQty: '',
    itemDirect_qty: '',
    uom: 'PC',
    expiryDate: '',
    remarks: '',
    poNumber: '',
    status: 'Active',
    itemDirect: true,
});

const UOM_OPTIONS = ['PC', 'ST', 'LI', 'GA', 'ME', 'BX', 'KG', 'PAIL', 'RL'];

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
export default function ItemDirect() {
    const [warehouses, setWarehouses] = useState([]);
    const [loadingWH, setLoadingWH] = useState(false);
    const [items, setItems] = useState([]);        // ← dito na
    const [loadingItems, setLoadingItems] = useState(false); // ← dito na
    const [submitting, setSubmitting] = useState(false)
    const [activeTab, setActiveTab] = useState('form');

    const [rows, setRows] = useState([emptyItem()]);
    const [headerForm, setHeaderForm] = useState({ poNumber: '', remarks: '', logDate: today() });

    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);
    const [historySearch, setHistorySearch] = useState('');

    // ── Fetch warehouses + history on mount ───────────────────────────────────
    useEffect(() => {
        (async () => {
            setLoadingWH(true);
            try {
                const res = await fetch(`${API_BASE_URL}api/WareHouse`);
                if (res.ok) setWarehouses(await res.json());
                const itemRes = await fetch(`${API_BASE_URL}api/Items`);
                if (itemRes.ok) setItems(await itemRes.json());
            } catch (err) { console.error(err); }
            finally { setLoadingWH(false); }
        })();
        fetchHistory();
    }, []);

    // ── Fetch history ─────────────────────────────────────────────────────────
const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
        const res = await fetch(`${API_BASE_URL}api/InventoryData/item-direct`);  // ✅ dedicated endpoint
        if (res.ok) {
            const data = await res.json();
            setHistory(data); // no need to filter anymore — already filtered in backend
        }
    } catch (err) { console.error(err); }
    finally { setLoadingHistory(false); }
};

    // ── Row update — QTY syncs availableQty + itemDirect_qty ─────────────────
    const updateRow = (idx, field, value) => {
        setRows(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };

            // warehouse code → id
            if (field === 'warehouseCode') {
                const wh = warehouses.find(w => w.wareHCode === value);
                next[idx].warehouseId = wh ? wh.id : null;
            }

            // ★ single qty input — auto-fill avail + direct
            if (field === 'quantity') {
                next[idx].availableQty = value;
                next[idx].itemDirect_qty = value;
            }

            return next;
        });
    };


    const addRow = () => setRows(prev => [...prev, emptyItem()]);
    const removeRow = (idx) => { if (rows.length > 1) setRows(prev => prev.filter((_, i) => i !== idx)); };
    const duplicateRow = (idx) => {
        setRows(prev => {
            const copy = { ...prev[idx], itemCode: '', itemDescription: '', batchNo: '', quantity: '', availableQty: '', itemDirect_qty: '' };
            const next = [...prev];
            next.splice(idx + 1, 0, copy);
            return next;
        });
    };

    // ── Validate ──────────────────────────────────────────────────────────────
    const validate = () => {
        for (let i = 0; i < rows.length; i++) {
            const r = rows[i];
            if (!r.itemCode.trim()) return `Row ${i + 1}: Item Code is required.`;
            if (!r.itemDescription.trim()) return `Row ${i + 1}: Item Description is required.`;
            if (!r.warehouseCode) return `Row ${i + 1}: Warehouse is required.`;
            if (r.quantity === '' || isNaN(Number(r.quantity)))
                return `Row ${i + 1}: Quantity is required.`;
        }
        return null;
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        const err = validate();
        if (err) {
            Swal.fire({ icon: 'warning', title: 'Validation Error', text: err, confirmButtonColor: '#2563eb' });
            return;
        }

        const confirm = await Swal.fire({
            title: `Post ${rows.length} Item Direct Record${rows.length > 1 ? 's' : ''}?`,
            text: 'This will add to InventoryData with ItemDirect = true.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#2563eb',
            cancelButtonColor: '#6b7280',
            confirmButtonText: 'Yes, Post!',
        });
        if (!confirm.isConfirmed) return;

        setSubmitting(true);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        let successCount = 0, failCount = 0;
        const errors = [];

        for (const row of rows) {
            try {
                const qty = parseFloat(row.quantity) || 0;
                const payload = {
                    itemCode: row.itemCode.trim().toUpperCase(),
                    itemDescription: row.itemDescription.trim().toUpperCase(),
                    warehouseCode: row.warehouseCode,
                    warehouseId: row.warehouseId,
                    batchNo: row.batchNo.trim() || null,
                    quantity: qty,
                    availableQty: qty,   // same as qty
                    reservedQty: parseFloat(row.reservedQty) || 0,
                    uom: row.uom || 'PC',
                    expiryDate: row.expiryDate || null,
                    status: row.status || 'Active',
                    remarks: [headerForm.remarks, row.remarks].filter(Boolean).join(' | ') || null,
                    poNumber: headerForm.poNumber || null,
                    lastUpdated: new Date().toISOString(),
                    lastUpdatedBy: user.id || user.userId || null,
                    itemDirect: true,
                    itemDirect_qty: qty,   // same as qty
                };

                const res = await fetch(`${API_BASE_URL}api/InventoryData`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });

                if (res.ok) {
                    successCount++;
                } else {
                    const body = await res.json().catch(() => ({}));
                    failCount++;
                    errors.push(`${row.itemCode}: ${body.message || 'Unknown error'}`);
                }
            } catch (e) {
                failCount++;
                errors.push(`${row.itemCode}: ${e.message}`);
            }
        }

        setSubmitting(false);

        if (successCount > 0) {
            await Swal.fire({
                icon: failCount > 0 ? 'warning' : 'success',
                title: failCount > 0
                    ? `${successCount} saved, ${failCount} failed`
                    : `${successCount} Record${successCount > 1 ? 's' : ''} Posted!`,
                html: errors.length > 0
                    ? `<div class="text-left text-sm text-red-600">${errors.join('<br>')}</div>`
                    : undefined,
                timer: failCount > 0 ? undefined : 2000,
                showConfirmButton: failCount > 0,
                confirmButtonColor: '#2563eb',
            });
            setRows([emptyItem()]);
            setHeaderForm({ poNumber: '', remarks: '', logDate: today() });
            fetchHistory();
            setActiveTab('history');
        } else {
            Swal.fire({
                icon: 'error',
                title: 'All Posts Failed',
                html: `<div class="text-left text-sm text-red-600">${errors.join('<br>')}</div>`,
                confirmButtonColor: '#ef4444',
            });
        }
    };

    // ── Filtered history ──────────────────────────────────────────────────────
    const filteredHistory = history.filter(h => {
        const q = historySearch.toLowerCase();
        return (
            h.itemCode?.toLowerCase().includes(q) ||
            h.itemDescription?.toLowerCase().includes(q) ||
            h.warehouseCode?.toLowerCase().includes(q) ||
            h.batchNo?.toLowerCase().includes(q)
        );
    });

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-100 p-6">
            <div className="max-w-8xl mx-auto space-y-5">

                {/* ── PAGE HEADER ── */}
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="bg-gradient-to-br from-blue-700 to-blue-900 p-3.5 rounded-xl shadow-lg shadow-blue-200">
                                <PackagePlus className="w-7 h-7 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Item Direct</h1>
                                <p className="text-slate-500 text-sm mt-0.5">
                                    Direct inventory posting —{' '}
                                    <span className="font-mono text-blue-700 font-semibold">ItemDirect = true</span>
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={fetchHistory}
                            className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-semibold text-sm transition-all"
                        >
                            <RefreshCw className="w-4 h-4" /> Refresh
                        </button>
                    </div>
                </div>

                {/* ── TABS ── */}
                <div className="flex gap-2">
                    {[
                        { key: 'form', icon: <ClipboardList className="w-4 h-4" />, label: 'New Entry' },
                        { key: 'history', icon: <List className="w-4 h-4" />, label: `Direct Records (${history.length})` },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm transition-all shadow-sm
                ${activeTab === tab.key
                                    ? 'bg-gradient-to-r from-blue-700 to-blue-800 text-white shadow-blue-200 shadow-md'
                                    : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'}`}
                        >
                            {tab.icon}{tab.label}
                        </button>
                    ))}
                </div>

                {/* ══════════════════════════════════════════════════════════════════ */}
                {/* TAB 1 — FORM                                                      */}
                {/* ══════════════════════════════════════════════════════════════════ */}
                {activeTab === 'form' && (
                    <div className="space-y-5">

                        {/* Header section */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
                            <h2 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4">
                                Header Info{' '}
                                <span className="text-slate-400 normal-case font-normal">(applies to all rows)</span>
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">PO / Reference No.</label>
                                    <input
                                        value={headerForm.poNumber}
                                        onChange={e => setHeaderForm(p => ({ ...p, poNumber: e.target.value }))}
                                        className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none font-mono"
                                        placeholder="Optional PO number"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Log Date</label>
                                    <input
                                        type="date"
                                        value={headerForm.logDate}
                                        onChange={e => setHeaderForm(p => ({ ...p, logDate: e.target.value }))}
                                        className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">General Remarks</label>
                                    <input
                                        value={headerForm.remarks}
                                        onChange={e => setHeaderForm(p => ({ ...p, remarks: e.target.value }))}
                                        className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                                        placeholder="Optional remarks"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Items table */}
                        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50">
                                <div className="flex items-center gap-2">
                                    <Box className="w-4 h-4 text-blue-600" />
                                    <h2 className="text-sm font-bold text-slate-800">Item Rows</h2>
                                    <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-200">
                                        {rows.length} row{rows.length > 1 ? 's' : ''}
                                    </span>
                                    {/* legend */}
                                    <span className="ml-2 flex items-center gap-1 text-xs text-slate-400">
                                        <span className="w-2 h-2 rounded-full bg-blue-500 inline-block"></span>
                                        Qty auto-fills Avail &amp; Direct Qty
                                    </span>
                                </div>
                                <button
                                    onClick={addRow}
                                    className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-bold transition-all"
                                >
                                    <Plus className="w-3.5 h-3.5" /> Add Row
                                </button>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs">
                                            <th className="px-3 py-3 text-left font-bold w-8">#</th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[120px]">
                                                Item Code <span className="text-red-300">*</span>
                                            </th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[200px]">
                                                Description <span className="text-red-300">*</span>
                                            </th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[160px]">
                                                Warehouse <span className="text-red-300">*</span>
                                            </th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[100px]">Batch No.</th>

                                            {/* ★ SINGLE QTY COLUMN */}
                                            <th className="px-3 py-3 text-left font-bold min-w-[110px] bg-blue-900">
                                                <div className="flex items-center gap-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block"></span>
                                                    Qty <span className="text-red-300">*</span>
                                                </div>
                                                <div className="text-blue-300 font-normal text-xs mt-0.5 tracking-normal normal-case">
                                                    = Avail = Direct
                                                </div>
                                            </th>

                                            <th className="px-3 py-3 text-left font-bold min-w-[70px]">UOM</th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[110px]">Expiry Date</th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[80px]">Status</th>
                                            <th className="px-3 py-3 text-left font-bold min-w-[130px]">Remarks</th>
                                            <th className="px-3 py-3 text-center font-bold w-16">Act</th>
                                        </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-100">
                                        {rows.map((row, idx) => (
                                            <tr
                                                key={idx}
                                                className={`hover:bg-blue-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                                            >
                                                {/* # */}
                                                <td className="px-3 py-2.5 text-slate-400 text-xs font-mono">{idx + 1}</td>

                                                {/* Item Code */}
                                                <td className="px-3 py-2.5">
                                                    <ItemCodeCell row={row} idx={idx} items={items} loadingItems={loadingItems} updateRow={updateRow} />

                                                </td>

                                                {/* Description */}
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        value={row.itemDescription}
                                                        onChange={e => updateRow(idx, 'itemDescription', e.target.value)}
                                                        className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Item description"
                                                    />
                                                </td>

                                                {/* Warehouse */}
                                                <td className="px-3 py-2.5">
                                                    <div className="relative">
                                                        {loadingWH ? (
                                                            <div className="flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs text-slate-400">
                                                                <Loader2 className="w-3 h-3 animate-spin" /> Loading...
                                                            </div>
                                                        ) : (
                                                            <select
                                                                value={row.warehouseCode}
                                                                onChange={e => updateRow(idx, 'warehouseCode', e.target.value)}
                                                                className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none bg-white appearance-none pr-7"
                                                            >
                                                                <option value="">Select WH</option>
                                                                {warehouses.map(wh => (
                                                                    <option key={wh.id} value={wh.wareHCode}>
                                                                        {wh.wareHCode} — {wh.wareHouseName}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        )}
                                                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                                                    </div>
                                                    {row.warehouseCode && (
                                                        <p className="text-xs text-green-600 font-semibold mt-0.5 flex items-center gap-1">
                                                            <Warehouse className="w-2.5 h-2.5" />
                                                            {warehouses.find(w => w.wareHCode === row.warehouseCode)?.wareHouseName || row.warehouseCode}
                                                        </p>
                                                    )}
                                                </td>

                                                {/* Batch No */}
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        value={row.batchNo}
                                                        onChange={e => updateRow(idx, 'batchNo', e.target.value)}
                                                        className="w-full px-2.5 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none font-mono"
                                                        placeholder="Optional"
                                                    />
                                                </td>

                                                {/* ★ SINGLE QTY INPUT */}
                                                <td className="px-3 py-2.5 bg-blue-50/50">
                                                    <input
                                                        type="number"
                                                        value={row.quantity}
                                                        onChange={e => updateRow(idx, 'quantity', e.target.value)}
                                                        className="w-full px-2.5 py-1.5 border-2 border-blue-500 rounded-lg text-xs focus:ring-2 focus:ring-blue-500 outline-none font-bold text-blue-900 bg-white"
                                                        placeholder="0"
                                                        min="0"
                                                    />
                                                    {row.quantity !== '' && (
                                                        <div className="flex gap-1 mt-1.5 flex-wrap">
                                                            <span className="text-xs bg-green-100 text-green-700 border border-green-200 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                                                                Avail: {row.quantity}
                                                            </span>
                                                            <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-1.5 py-0.5 rounded font-semibold whitespace-nowrap">
                                                                Direct: {row.quantity}
                                                            </span>
                                                        </div>
                                                    )}
                                                </td>

                                                {/* UOM */}
                                                <td className="px-3 py-2.5">
                                                    <select
                                                        value={row.uom}
                                                        onChange={e => updateRow(idx, 'uom', e.target.value)}
                                                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none bg-white font-mono font-bold"
                                                    >
                                                        {UOM_OPTIONS.map(u => <option key={u} value={u}>{u}</option>)}
                                                    </select>
                                                </td>

                                                {/* Expiry Date */}
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        type="date"
                                                        value={row.expiryDate}
                                                        onChange={e => updateRow(idx, 'expiryDate', e.target.value)}
                                                        className="w-full px-2 py-1.5 border border-slate-200 bg-slate-50 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                    />
                                                </td>

                                                {/* Status */}
                                                <td className="px-3 py-2.5">
                                                    <select
                                                        value={row.status}
                                                        onChange={e => updateRow(idx, 'status', e.target.value)}
                                                        className="w-full px-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none bg-white"
                                                    >
                                                        {['Active', 'Quarantined', 'Reserved', 'Expired'].map(s => (
                                                            <option key={s} value={s}>{s}</option>
                                                        ))}
                                                    </select>
                                                </td>

                                                {/* Row Remarks */}
                                                <td className="px-3 py-2.5">
                                                    <input
                                                        value={row.remarks}
                                                        onChange={e => updateRow(idx, 'remarks', e.target.value)}
                                                        className="w-full px-2.5 py-1.5 border border-slate-200 bg-slate-50 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none"
                                                        placeholder="Optional"
                                                    />
                                                </td>

                                                {/* Actions */}
                                                <td className="px-3 py-2.5">
                                                    <div className="flex items-center gap-1 justify-center">
                                                        <button
                                                            onClick={() => duplicateRow(idx)}
                                                            className="p-1 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-all"
                                                            title="Duplicate row"
                                                        >
                                                            <Plus className="w-3.5 h-3.5" />
                                                        </button>
                                                        {rows.length > 1 && (
                                                            <button
                                                                onClick={() => removeRow(idx)}
                                                                className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-all"
                                                                title="Remove row"
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer summary */}
                            <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
                                <div className="flex items-center gap-4 text-xs text-slate-500">
                                    <span>{rows.length} row{rows.length > 1 ? 's' : ''}</span>
                                    <span>·</span>
                                    <span>
                                        Total Qty:{' '}
                                        <strong className="text-blue-700">
                                            {rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0)}
                                        </strong>
                                    </span>
                                    <span>·</span>
                                    <span className="text-green-700">
                                        Avail = <strong>{rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0)}</strong>
                                    </span>
                                    <span>·</span>
                                    <span className="text-amber-700">
                                        Direct = <strong>{rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0), 0)}</strong>
                                    </span>
                                </div>
                                <button
                                    onClick={addRow}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-bold transition"
                                >
                                    <Plus className="w-3 h-3" /> Add Row
                                </button>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setRows([emptyItem()]);
                                    setHeaderForm({ poNumber: '', remarks: '', logDate: today() });
                                }}
                                className="px-6 py-3 border border-slate-300 text-slate-600 rounded-xl font-semibold text-sm hover:bg-slate-50 transition"
                            >
                                Clear
                            </button>
                            <button
                                onClick={handleSubmit}
                                disabled={submitting}
                                className="flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900 disabled:opacity-50 text-white rounded-xl font-bold text-sm transition-all shadow-lg hover:shadow-xl"
                            >
                                {submitting
                                    ? <><Loader2 className="w-4 h-4 animate-spin" /> Posting...</>
                                    : <><Save className="w-4 h-4" /> Post {rows.length} Record{rows.length > 1 ? 's' : ''}</>}
                            </button>
                        </div>
                    </div>
                )}

                {/* ══════════════════════════════════════════════════════════════════ */}
                {/* TAB 2 — HISTORY                                                   */}
                {/* ══════════════════════════════════════════════════════════════════ */}
                {activeTab === 'history' && (
                    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-5 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                            <div className="relative max-w-sm flex-1">
                                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <input
                                    type="text"
                                    placeholder="Search item code, description, warehouse..."
                                    value={historySearch}
                                    onChange={e => setHistorySearch(e.target.value)}
                                    className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                                />
                            </div>
                            <div className="flex items-center gap-3 ml-4">
                                <span className="text-xs text-slate-500 font-semibold">
                                    {filteredHistory.length} record{filteredHistory.length !== 1 ? 's' : ''}
                                </span>
                                <button
                                    onClick={fetchHistory}
                                    disabled={loadingHistory}
                                    className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-xl text-sm font-semibold transition"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${loadingHistory ? 'animate-spin' : ''}`} />
                                </button>
                            </div>
                        </div>

                        {loadingHistory ? (
                            <div className="flex items-center justify-center py-20">
                                <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                            </div>
                        ) : filteredHistory.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                                <PackagePlus className="w-12 h-12 mb-3 opacity-30" />
                                <p className="font-semibold text-slate-500">No Item Direct records yet</p>
                                <p className="text-sm mt-1">Post your first entry using the New Entry tab</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="bg-gradient-to-r from-blue-700 to-blue-800 text-white text-xs uppercase tracking-wider">
                                            {[
                                                '#', 'Item Code', 'Description', 'Warehouse', 'Batch',
                                                'Qty', 'Avail Qty', 'Direct Qty', 'UOM', 'Status',
                                                'Direct', 'Last Updated'
                                            ].map((h, i) => (
                                                <th key={i} className="px-4 py-3.5 text-left font-bold whitespace-nowrap">{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100">
                                        {filteredHistory.map((item, idx) => (
                                            <tr
                                                key={item.id}
                                                className={`hover:bg-blue-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/30'}`}
                                            >
                                                <td className="px-4 py-3 text-slate-400 text-xs font-mono">{idx + 1}</td>
                                                <td className="px-4 py-3">
                                                    <span className="font-mono font-bold text-blue-900 text-sm">{item.itemCode}</span>
                                                </td>
                                                <td className="px-4 py-3 text-slate-700 max-w-[180px] truncate">{item.itemDescription}</td>
                                                <td className="px-4 py-3">
                                                    <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-lg text-xs font-semibold border border-slate-200">
                                                        <Warehouse className="w-3 h-3" />{item.warehouseCode}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className="font-mono text-xs text-slate-500">{item.batchNo || '—'}</span>
                                                </td>
                                                {/* Qty */}
                                                <td className="px-4 py-3 font-bold text-slate-800">{item.quantity}</td>
                                                {/* Avail Qty */}
                                                <td className="px-4 py-3 font-bold text-green-700">{item.availableQty ?? '—'}</td>
                                                {/* Direct Qty */}
                                                <td className="px-4 py-3">
                                                    <span className="bg-amber-100 text-amber-800 border border-amber-300 px-2.5 py-0.5 rounded-lg text-xs font-black">
                                                        {item.itemDirect_qty ?? 0}
                                                    </span>
                                                </td>
                                                {/* UOM */}
                                                <td className="px-4 py-3">
                                                    <span className="bg-indigo-100 text-indigo-800 border border-indigo-200 px-2 py-0.5 rounded text-xs font-bold font-mono">
                                                        {item.uom || '—'}
                                                    </span>
                                                </td>
                                                {/* Status */}
                                                <td className="px-4 py-3">
                                                    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold border
                            ${item.status === 'Active' ? 'bg-green-50 text-green-700 border-green-200'
                                                            : item.status === 'Expired' ? 'bg-red-50 text-red-700 border-red-200'
                                                                : item.status === 'Quarantined' ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                                                                    : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                                        {item.status || 'Active'}
                                                    </span>
                                                </td>
                                                {/* ItemDirect bool */}
                                                <td className="px-4 py-3">
                                                    {item.itemDirect ? (
                                                        <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-full text-xs font-bold">
                                                            <CheckCircle className="w-3 h-3" /> True
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-500 border border-slate-200 px-2 py-0.5 rounded-full text-xs font-bold">
                                                            False
                                                        </span>
                                                    )}
                                                </td>
                                                {/* Last Updated */}
                                                <td className="px-4 py-3 text-xs text-slate-500">
                                                    {item.lastUpdated
                                                        ? new Date(item.lastUpdated).toLocaleDateString('en-US', {
                                                            month: 'short', day: '2-digit', year: 'numeric'
                                                        })
                                                        : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr className="bg-blue-50 border-t-2 border-blue-200">
                                            <td colSpan={5} className="px-4 py-3 text-xs font-bold text-blue-700 uppercase">TOTAL</td>
                                            <td className="px-4 py-3 font-black text-slate-800">
                                                {filteredHistory.reduce((s, i) => s + (Number(i.quantity) || 0), 0).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 font-black text-green-700">
                                                {filteredHistory.reduce((s, i) => s + (Number(i.availableQty) || 0), 0).toLocaleString()}
                                            </td>
                                            <td className="px-4 py-3 font-black text-amber-700">
                                                {filteredHistory.reduce((s, i) => s + (Number(i.itemDirect_qty) || 0), 0).toLocaleString()}
                                            </td>
                                            <td colSpan={4} />
                                        </tr>
                                    </tfoot>
                                </table>
                            </div>
                        )}
                    </div>
                )}

            </div>
        </div>
    );
}
// ─── ITEM CODE SEARCH DROPDOWN ───
function ItemCodeCell({ row, idx, items, loadingItems, updateRow }) {
    const [query, setQuery] = useState(row.itemCode);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => { setQuery(row.itemCode); }, [row.itemCode]);

    useEffect(() => {
        const handler = (e) => { if (!ref.current?.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const filtered = items.filter(it =>
        it.itemCode?.toLowerCase().includes(query.toLowerCase()) ||
        it.itemDesciption?.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 30);

    const handleSelect = (item) => {
        setQuery(item.itemCode);
        setOpen(false);
        updateRow(idx, 'itemCode', item.itemCode);
        updateRow(idx, 'itemDescription', item.itemDesciption);
        updateRow(idx, 'uom', item.oumSystem || 'PC');
    };

    return (
        <div ref={ref} className="relative">
            <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                <input
                    value={query}
                    onChange={e => { setQuery(e.target.value.toUpperCase()); setOpen(true); updateRow(idx, 'itemCode', e.target.value.toUpperCase()); }}
                    onFocus={() => setOpen(true)}
                    className="w-full pl-7 pr-2 py-1.5 border border-slate-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 outline-none font-mono uppercase"
                    placeholder="Search…"
                />
            </div>

            {open && query.length >= 1 && (
                <div
                    className="fixed z-[9999] w-72 bg-white border border-slate-200 rounded-xl shadow-xl max-h-52 overflow-y-auto"
                    style={{
                        top: ref.current ? ref.current.getBoundingClientRect().bottom + 4 : 0,
                        left: ref.current ? ref.current.getBoundingClientRect().left : 0,
                    }}
                >
                    {loadingItems ? (
                        <div className="flex items-center justify-center py-4 text-xs text-slate-400 gap-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="py-4 text-center text-xs text-slate-400">No match</div>
                    ) : filtered.map(item => (
                        <button
                            key={item.id}
                            onMouseDown={() => handleSelect(item)}
                            className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-50 last:border-0"
                        >
                            <span className="font-mono font-bold text-blue-800 text-xs">{item.itemCode}</span>
                            <span className="block text-xs text-slate-500 truncate">{item.itemDesciption}</span>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}